import React, { useEffect, useMemo, useState } from "react";
import TrustdService from "../service/TrustdService";
import { formatDate, useHostSummary } from "../hooks";
import { useAppContext } from "../context/AppContext";
import { useNavigate } from "../router";

const metricCards = [
  {
    key: "TotalVisits",
    filterType: "all",
    title: "Total Guests",
    icon: "pi pi-users",
    tone: "blue",
    detail: (info) =>
      `+${Number(info?.CurrentVisits || 0) + Number(info?.FutureVisits || 0)} active/upcoming`,
  },
  {
    key: "CompletedVisits",
    filterType: "completed",
    title: "Completed Stay",
    icon: "pi pi-verified",
    tone: "emerald",
    detail: (info) => {
      const total = Number(info?.TotalVisits || 0);
      const completed = Number(info?.CompletedVisits || 0);
      return total
        ? `${Math.round((completed / total) * 100)}% completion`
        : "No completed stays yet";
    },
  },
  {
    key: "CurrentVisits",
    filterType: "current",
    title: "Currently Staying",
    icon: "pi pi-clock",
    tone: "amber",
    detail: (info) => `${Number(info?.CurrentVisits || 0)} active now`,
  },
  {
    key: "FutureVisits",
    filterType: "future",
    title: "Upcoming Stay",
    icon: "pi pi-calendar",
    tone: "violet",
    detail: () => "Check upcoming arrivals",
  },
];

function getGuestName(review) {
  return (
    [review?.first_name, review?.middle_name, review?.last_name]
      .filter(Boolean)
      .join(" ") || "Guest"
  );
}

function getInitials(name) {
  return (
    String(name || "Guest")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("") || "G"
  );
}

function getReviewStatus(review) {
  const today = new Date();
  const checkIn = review?.check_in_date ? new Date(review.check_in_date) : null;
  const checkOut = review?.check_out_date
    ? new Date(review.check_out_date)
    : null;

  if (checkOut && !Number.isNaN(checkOut.getTime()) && checkOut < today)
    return { label: "Completed", tone: "emerald" };
  if (
    checkIn &&
    checkOut &&
    !Number.isNaN(checkIn.getTime()) &&
    !Number.isNaN(checkOut.getTime()) &&
    checkIn <= today &&
    checkOut >= today
  ) {
    return { label: "Staying", tone: "amber" };
  }
  return { label: "Upcoming", tone: "blue" };
}

export default function Dashboard() {
  const [dashboardInfo, setDashboardInfo] = useState(null);
  const [recentGuests, setRecentGuests] = useState([]);
  const [loadingDashboardInfo, setLoadingDashboardInfo] = useState(false);
  const { username, credit, detailedUser } = useHostSummary();
  const { commit } = useAppContext();
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      setLoadingDashboardInfo(true);
      try {
        const [info, reviews] = await Promise.all([
          TrustdService.getDashboardInformationByHostEmail(),
          TrustdService.listReviewsByHostEmail().catch(() => []),
        ]);
        setDashboardInfo(info || {});
        setRecentGuests(Array.isArray(reviews) ? reviews.slice(0, 4) : []);
      } catch (error) {
        console.error("Dashboard load failed:", error);
      } finally {
        setLoadingDashboardInfo(false);
      }
    }
    load();
  }, []);

  const firstName = useMemo(() => {
    const normalized = String(
      username || detailedUser?.contactName || "",
    ).trim();
    return normalized ? normalized.split(/\s+/)[0] : "Host";
  }, [detailedUser?.contactName, username]);

  const channelName = String(detailedUser?.channel || "").trim();
  const formattedChannelName = channelName
    ? `${channelName.charAt(0).toUpperCase()}${channelName.slice(1)}`
    : "";

  const goToReviews = (filterType = "all") => {
    commit("updatePastReviewsFilter", filterType);
    navigate(`/reviews?filter=${encodeURIComponent(filterType)}`);
  };

  return (
    <div className="dashboard-page dashboard-modern">
      <section className="dashboard-credit-band">
        <p>Screening Credits</p>
        <div>
          <strong>{credit}</strong>
          <button type="button" onClick={() => navigate("/topup")}>
            <i className="pi pi-plus" aria-hidden="true" />
            <span>Top Up</span>
          </button>
        </div>
      </section>

      <section className="dashboard-modern-grid" aria-label="Guest overview">
        {metricCards.map((card) => (
          <button
            type="button"
            key={card.key}
            className="dashboard-modern-card"
            onClick={() => goToReviews(card.filterType)}
          >
            <span className={`dashboard-modern-icon is-${card.tone}`}>
              <i className={card.icon} aria-hidden="true" />
            </span>
            <strong>
              {loadingDashboardInfo ? "..." : dashboardInfo?.[card.key] || "0"}
            </strong>
            <span className="dashboard-modern-card-title">{card.title}</span>
            <span className="dashboard-modern-card-note">
              {card.detail(dashboardInfo)}
            </span>
          </button>
        ))}
      </section>

      <section className="dashboard-recent-panel">
        <div className="dashboard-recent-head">
          <h2>Recent Guests</h2>
          <button type="button" onClick={() => goToReviews("all")}>
            View all <i className="pi pi-angle-right" aria-hidden="true" />
          </button>
        </div>
        <div className="dashboard-recent-list">
          {loadingDashboardInfo ? (
            <div className="dashboard-recent-empty">Loading guests...</div>
          ) : null}
          {!loadingDashboardInfo && recentGuests.length === 0 ? (
            <div className="dashboard-recent-empty">No recent guests yet.</div>
          ) : null}
          {!loadingDashboardInfo &&
            recentGuests.map((review, index) => {
              const name = getGuestName(review);
              const status = getReviewStatus(review);
              return (
                <button
                  type="button"
                  className="dashboard-recent-row"
                  key={
                    review.trustd_reference ||
                    `${name}-${review.check_in_date || review.check_out_date || index}`
                  }
                  onClick={() => goToReviews("all")}
                >
                  <span className="dashboard-recent-avatar">
                    {getInitials(name)}
                  </span>
                  <span className="dashboard-recent-main">
                    <strong>{name}</strong>
                    <span>
                      {formatDate(
                        review.check_in_date || review.check_out_date,
                      )}
                    </span>
                  </span>
                  <span className={`dashboard-recent-status is-${status.tone}`}>
                    {status.label}
                  </span>
                </button>
              );
            })}
        </div>
      </section>
    </div>
  );
}
